import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faGlobe, faTimes } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { toast } from "react-toastify";
import { isoToFlag } from "../util";
import AddLanguagePopup from "./AddLanguagePopup";
import RemoveLanguage from "./RemoveLanguage";
import { useLanguage } from "../context/LanguageContext";
import { usePermission } from "../context/PermissionContext";
import { useRemoveLanguage } from "../queries/use-remove-language";
import { useLanguageRemovalPreview } from "../queries/use-language-removal-preview";

const Languages = ({ projectId, baseLanguageId }) => {
  const [showForm, setShowForm] = React.useState(false);
  const [languageToRemove, setLanguageToRemove] = React.useState(null);
  const { languages, displayLanguages, toggleDisplayLanguage } = useLanguage();
  const { hasAdminRight } = usePermission();
  const {
    mutateAsync: removeLanguage,
    isLoading: removeLoading,
    error: removeError,
    reset: resetRemoveMutation,
  } = useRemoveLanguage(projectId);
  const {
    data: removalPreview,
    isLoading: previewLoading,
    isError: previewError,
  } = useLanguageRemovalPreview(
    projectId,
    languageToRemove?.id,
    Boolean(languageToRemove)
  );

  const visibleCount = (displayLanguages ?? []).length;
  const totalCount = languages.length;

  const toggleAll = () => {
    const shouldShowAll = visibleCount < totalCount;
    languages.forEach((lang) => {
      const isVisible = displayLanguages?.includes(lang.id);
      if (shouldShowAll && !isVisible) toggleDisplayLanguage(lang.id);
      if (!shouldShowAll && isVisible) toggleDisplayLanguage(lang.id);
    });
  };

  const closeRemovePopup = () => {
    setLanguageToRemove(null);
    resetRemoveMutation();
  };

  const confirmRemove = async () => {
    if (!languageToRemove) return;
    const name = languageToRemove.name;
    try {
      const result = await removeLanguage(languageToRemove.id);
      const assignments = result?.deletedContributorAssignments ?? 0;
      const message =
        assignments > 0
          ? `Removed ${name} from project — ${assignments} contributor assignment${
              assignments === 1 ? "" : "s"
            } cleared`
          : `Removed ${name} from project`;
      toast.success(message);
      closeRemovePopup();
    } catch (e) {
      // Keep the popup open so the user can read the inline error and retry.
    }
  };

  const removeErrorCode = removeError?.response?.data?.code ?? null;

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-3 mb-4 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-1.5 mr-1">
          <FontAwesomeIcon icon={faGlobe} className="w-3 h-3" />
          Languages
          <span className="text-gray-400 normal-case tracking-normal font-normal">
            ({visibleCount}/{totalCount})
          </span>
        </span>
        {languages.map((language) => {
          const isVisible =
            !displayLanguages || displayLanguages.includes(language.id);
          const isBase = language.id === baseLanguageId;
          const showRemove = hasAdminRight && !isBase;
          return (
            <div
              key={language.id}
              className={classNames(
                "inline-flex items-stretch h-8 rounded-full border text-xs font-medium transition overflow-hidden",
                isVisible
                  ? "bg-primary bg-opacity-10 text-gray-800 border-primary"
                  : "bg-white text-gray-400 border-gray-200 hover:border-gray-300"
              )}
            >
              <button
                type="button"
                onClick={() => toggleDisplayLanguage(language.id)}
                title={language.name}
                aria-label={`Toggle visibility of ${language.name}`}
                className="inline-flex items-center gap-1.5 px-3"
              >
                <span className="text-base leading-none">
                  {isoToFlag(language.iso)}
                </span>
                <span className="uppercase tracking-wide">{language.iso}</span>
              </button>
              {showRemove && (
                <button
                  type="button"
                  onClick={() => setLanguageToRemove(language)}
                  title={`Remove ${language.name}`}
                  aria-label={`Remove ${language.name}`}
                  className="inline-flex items-center justify-center px-2 text-gray-400 hover:text-red-600 hover:bg-red-50 border-l border-current border-opacity-20"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                </button>
              )}
              {hasAdminRight && isBase && (
                <span
                  className="group relative inline-flex items-center px-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 border-l border-current border-opacity-20 cursor-default select-none"
                  title="This is the project's base language — it cannot be removed."
                  aria-label={`${language.name} is the base language and cannot be removed`}
                  data-testid={`base-language-indicator-${language.id}`}
                >
                  base
                  <span
                    role="tooltip"
                    className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[11px] font-normal normal-case tracking-normal text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity shadow-lg"
                  >
                    Base language — cannot be removed.
                  </span>
                </span>
              )}
            </div>
          );
        })}
        {totalCount > 0 && (
          <button
            type="button"
            onClick={toggleAll}
            className="ml-1 text-xs text-gray-500 hover:text-gray-800 underline"
          >
            {visibleCount < totalCount ? "Show all" : "Hide all"}
          </button>
        )}
        {hasAdminRight && (
          <button
            type="button"
            title="Add language"
            aria-label="Add language"
            className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-white bg-primary text-xs font-semibold shadow-sm hover:opacity-95 transition"
            onClick={() => setShowForm(true)}
          >
            <FontAwesomeIcon icon={faPlus} className="w-3 h-3" />
            Add language
          </button>
        )}
      </div>
      {showForm && (
        <AddLanguagePopup
          projectId={projectId}
          onClose={() => setShowForm(false)}
        />
      )}
      {languageToRemove && (
        <RemoveLanguage
          language={languageToRemove}
          onCancel={closeRemovePopup}
          onConfirm={confirmRemove}
          translationsToDelete={removalPreview?.translationsToDelete}
          countLoading={previewLoading}
          countError={previewError}
          submitting={removeLoading}
          submitErrorCode={removeErrorCode}
          hasSubmitError={Boolean(removeError)}
        />
      )}
    </>
  );
};
export default Languages;
